import React, { useState } from 'react';
import { motion } from 'motion/react';
import { 
  Phone, 
  CheckCircle2,
  Send,
  Loader2,
  Mail,
  MessageCircle
} from 'lucide-react';
import { helpFormOptions, contactInfo } from '../data/mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestHelp = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    city: '',
    helpFor: '',
    helpType: '',
    contactMethod: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Get human-readable labels for values
  const getHelpForLabel = (value) => {
    const option = helpFormOptions.helpFor.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getHelpTypeLabel = (value) => {
    const option = helpFormOptions.helpTypes.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getContactMethodLabel = (value) => {
    const option = helpFormOptions.contactMethods.find(o => o.value === value);
    return option ? option.label : value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/help-requests`, {
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        help_for: formData.helpFor,
        help_type: formData.helpType,
        contact_method: formData.contactMethod,
        notes: formData.notes
      });
      
      setSubmittedData({
        ...response.data,
        helpForLabel: getHelpForLabel(formData.helpFor),
        helpTypeLabel: getHelpTypeLabel(formData.helpType),
        contactMethodLabel: getContactMethodLabel(formData.contactMethod)
      });
      toast.success('Request submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate WhatsApp message with request summary
  const generateWhatsAppMessage = () => {
    if (!submittedData) return '';
    
    const message = `Hello Senior's Desk,

I just submitted a help request and wanted to follow up.

*Request Summary:*
• Name: ${submittedData.full_name}
• Phone: ${submittedData.phone}
• City: ${submittedData.city}
• Help For: ${submittedData.helpForLabel}
• Type of Help: ${submittedData.helpTypeLabel}
• Preferred Contact: ${submittedData.contactMethodLabel}
${submittedData.notes ? `• Notes: ${submittedData.notes}` : ''}

Looking forward to hearing from you.`;
    
    return encodeURIComponent(message);
  };

  // Generate email body with request summary
  const generateEmailBody = () => {
    if (!submittedData) return '';
    
    const body = `Hello Senior's Desk,

I just submitted a help request through your website and wanted to follow up.

Request Summary:
- Name: ${submittedData.full_name}
- Phone: ${submittedData.phone}
- Email: ${submittedData.email}
- City: ${submittedData.city}
- Help For: ${submittedData.helpForLabel}
- Type of Help: ${submittedData.helpTypeLabel}
- Preferred Contact: ${submittedData.contactMethodLabel}
${submittedData.notes ? `- Additional Notes: ${submittedData.notes}` : ''}

Looking forward to hearing from you.

Best regards,
${submittedData.full_name}`;
    
    return encodeURIComponent(body);
  };

  if (submittedData) {
    return (
      <div className="min-h-screen pt-20 bg-[#FFF9F2]">
        <Toaster position="top-center" richColors />
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-20">
          <motion.div
            className="bg-white rounded-2xl p-10 text-center shadow-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="w-20 h-20 rounded-full bg-[#b8d1ba] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#232323]" />
            </div>
            <h1 className="heading-1 mb-4">Thank You!</h1>
            <p className="body-large mb-8">
              We've received your request. Our team will contact you within our operating hours (9:00 AM – 9:00 PM).
            </p>

            {/* Request Summary */}
            <div className="bg-[#FFF9F2] rounded-xl p-6 mb-8 text-left">
              <p className="font-medium text-[#232323] mb-4">Your Request Summary:</p>
              <div className="space-y-2 text-sm text-[#353535]">
                <p><span className="font-medium">Name:</span> {submittedData.full_name}</p>
                <p><span className="font-medium">Phone:</span> {submittedData.phone}</p>
                <p><span className="font-medium">City:</span> {submittedData.city}</p>
                <p><span className="font-medium">Help For:</span> {submittedData.helpForLabel}</p>
                <p><span className="font-medium">Type of Help:</span> {submittedData.helpTypeLabel}</p>
                <p><span className="font-medium">Preferred Contact:</span> {submittedData.contactMethodLabel}</p>
                {submittedData.notes && (
                  <p><span className="font-medium">Notes:</span> {submittedData.notes}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href={`https://web.whatsapp.com/send?phone=${contactInfo.whatsapp}&text=${generateWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#20bd5a] transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp
              </a>
              <a
                href={`mailto:${contactInfo.email}?subject=Help Request Follow-up - ${submittedData.full_name}&body=${generateEmailBody()}`}
                className="inline-flex items-center justify-center gap-2 bg-[#232323] text-white rounded-full px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#353535] transition-colors"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>

            {/* What happens next */}
            <div className="bg-[#E4EDF8] rounded-xl p-6 text-left">
              <p className="font-medium text-[#232323] mb-3">What happens next?</p>
              <ul className="space-y-2 text-[#353535] text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-mono">1.</span>
                  Our team will review your request
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-mono">2.</span>
                  We'll contact you via your preferred method
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-mono">3.</span>
                  We'll discuss your needs and share expected cost
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-mono">4.</span>
                  Once confirmed, we'll schedule the service
                </li>
              </ul>
            </div>

            <p className="text-sm text-[#666] mt-6">
              Need urgent help? Call us directly at{' '}
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#232323] font-medium underline">
                {contactInfo.phone}
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <Toaster position="top-center" richColors />
      
      {/* Hero */}
      <section 
        className="py-16"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <motion.h1
              className="heading-hero mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Request Concierge Help
            </motion.h1>
            <motion.p
              className="body-large"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Fill out this form and we'll get back to you within a few hours 
              to discuss how we can help.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-[#FFF9F2]">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <motion.form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <Label htmlFor="fullName" className="form-label">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="form-input"
                />
              </div>

              {/* Phone & Email Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="form-label">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="form-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="form-label">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="form-input"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <Label htmlFor="city" className="form-label">
                  City <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter your city"
                  className="form-input"
                />
              </div>

              {/* Help For */}
              <div>
                <Label className="form-label">
                  Who is this for? <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.helpFor}
                  onValueChange={(value) => handleSelectChange('helpFor', value)}
                  required
                >
                  <SelectTrigger className="form-input h-auto">
                    <SelectValue placeholder="Select who needs help" />
                  </SelectTrigger>
                  <SelectContent>
                    {helpFormOptions.helpFor.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Help Type */}
              <div>
                <Label className="form-label">
                  Type of help needed <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.helpType}
                  onValueChange={(value) => handleSelectChange('helpType', value)}
                  required
                >
                  <SelectTrigger className="form-input h-auto">
                    <SelectValue placeholder="Select type of assistance" />
                  </SelectTrigger>
                  <SelectContent>
                    {helpFormOptions.helpTypes.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contact Method */}
              <div>
                <Label className="form-label">
                  Preferred contact method <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.contactMethod}
                  onValueChange={(value) => handleSelectChange('contactMethod', value)}
                  required
                >
                  <SelectTrigger className="form-input h-auto">
                    <SelectValue placeholder="How should we reach you?" />
                  </SelectTrigger>
                  <SelectContent>
                    {helpFormOptions.contactMethods.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Notes */}
              <div>
                <Label htmlFor="notes" className="form-label">
                  Additional Notes
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Tell us more about your situation or any specific requirements..."
                  className="form-input min-h-[120px]"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full gap-2 text-base py-4"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>

              {/* Privacy Note */}
              <p className="text-sm text-[#666] text-center">
                Your information is safe with us. We'll only use it to contact you 
                about your request.
              </p>
            </div>
          </motion.form>

          {/* Alternative Contact */}
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-[#666] mb-4">Prefer to talk directly?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="btn-secondary gap-2"
              >
                <Phone className="w-4 h-4" />
                Call {contactInfo.phone}
              </a>
              <a
                href={`https://web.whatsapp.com/send?phone=${contactInfo.whatsapp}&text=${encodeURIComponent('Hello, I would like to request concierge support for a senior.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary gap-2"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RequestHelp;
